import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TestPlan, Feature, Component, Session, TestCase } from '../../types';
import { theme } from '../../theme';
import { formatDate } from '../../utils/date';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import SessionDetails from '../sessions/SessionDetails';

interface TestPlanDetailsProps {
  scope: TestPlan;
  onClose: () => void;
  features: Feature[];
  components: Component[];
  onEdit: (testPlan: TestPlan) => void;
  onComplete?: (testPlanId: string, newStatus?: 'draft' | 'active' | 'completed') => Promise<void> | void;
  onStatusChange?: (testPlanId: string, newStatus: 'draft' | 'active' | 'completed') => Promise<void> | void;
  onReopen?: (testPlanId: string) => void;
  onDelete: (testPlanId: string) => void;
  onRunSession?: (testPlan: TestPlan) => void;
  onBack?: () => void; // Optional callback to go back (e.g., to session details)
  zIndex?: number;
  milestones?: Record<string, { startDate: string; endDate?: string }>;
  projectCode?: string; // Project code prefix for TC ID display (e.g., "FS" -> "FS-TC-42")
}

const TestPlanDetails: React.FC<TestPlanDetailsProps> = ({
  scope,
  onClose,
  features,
  components,
  onEdit,
  onComplete,
  onStatusChange,
  onReopen,
  onDelete,
  onRunSession,
  onBack,
  zIndex = 1000,
  milestones = {},
  projectCode
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role || 'user';
  const [linkedSessions, setLinkedSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  
  const scopeComponents = (scope.components || []).map(id => 
    components.find(c => c.id === id)
  ).filter(Boolean) as Component[];

  // Fetch completed sessions linked to this test plan
  useEffect(() => {
    const fetchLinkedSessions = async () => {
      try {
        const testPlanUrl = `${window.location.origin}/testplan/${scope.id}`;
        const allSessions = await apiRequest<Session[]>('/api/sessions', {}, { useAuth: true });
        const linked = allSessions.filter(s => 
          s.testPlanUrl === testPlanUrl && s.status === 'completed'
        );
        setLinkedSessions(linked);
      } catch (error) {
        console.error('Error fetching linked sessions:', error);
      }
    };

    fetchLinkedSessions();
  }, [scope.id]);

  // Fetch test cases for all components in this test plan
  useEffect(() => {
    if (scopeComponents.length === 0) {
      setTestCases([]);
      return;
    }

    const fetchTestCases = async () => {
      try {
        const allTestCases: TestCase[] = [];
        for (const component of scopeComponents) {
          try {
            const componentTestCases = await apiRequest<TestCase[]>(
              `/api/testcases?componentId=${component.id}`,
              {},
              { useAuth: true }
            );
            if (Array.isArray(componentTestCases)) {
              allTestCases.push(...componentTestCases);
            }
          } catch (error) {
            console.error(`Error fetching test cases for component ${component.id}:`, error);
          }
        }
        setTestCases(allTestCases);
      } catch (error) {
        console.error('Error fetching test cases:', error);
        setTestCases([]);
      }
    };

    fetchTestCases();
  }, [scope.components, scopeComponents]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Get test cases for a specific component
  const getTestCasesForComponent = (componentId: string): TestCase[] => {
    return testCases.filter(tc => tc.componentId === componentId);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: zIndex,
        padding: '2rem',
        overflow: 'auto'
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          zIndex: zIndex + 1,
          position: 'relative',
          backgroundColor: theme.surface,
          border: `1px solid ${theme.gridLine}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${theme.gridLine}`
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', color: theme.textPrimary }}>
              Test Plan Details
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {onBack && (
                <button
                  className="btn btn-secondary"
                  onClick={onBack}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  ← Back to Session
                </button>
              )}
              {scope.status === 'completed' && onReopen && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reopen this test plan? It will be moved to "Active" status.')) {
                      onReopen(scope.id);
                    }
                  }}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  Reopen
                </button>
              )}
              <button
                className="btn btn-ghost btn-icon"
                onClick={onClose}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
          </div>
        </div>

      <div style={{ padding: '1.5rem' }}>
        {/* Basic Information */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: `1px solid ${theme.gridLine}`
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
              Test Plan ID
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
              {scope.id}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
              Status
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
              <span className={`status-badge status-${scope.status || 'active'}`}>
                {(scope.status || 'active').toUpperCase()}
              </span>
            </div>
          </div>
          {scope.createdAt && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Created
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {formatDate(scope.createdAt, 'MMM dd, yyyy h:mm a')}
              </div>
            </div>
          )}
          {scope.updatedAt && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Updated
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {formatDate(scope.updatedAt, 'MMM dd, yyyy h:mm a')}
              </div>
            </div>
          )}
          {scope.testingPlan && scope.testingPlan.length > 0 && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Platforms
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {scope.testingPlan.join(', ')}
              </div>
            </div>
          )}
          {scope.milestone && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Milestone
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {scope.milestone}
              </div>
              {milestones[scope.milestone] && (
                <div style={{ fontSize: '0.75rem', color: theme.textSecondary, marginTop: '0.25rem' }}>
                  {(() => {
                    const milestoneMeta = milestones[scope.milestone];
                    const startDate = formatDate(milestoneMeta.startDate, 'MMM dd, yyyy');
                    const endDate = milestoneMeta.endDate ? formatDate(milestoneMeta.endDate, 'MMM dd, yyyy') : null;
                    return endDate ? `${startDate} - ${endDate}` : `Started: ${startDate}`;
                  })()}
                </div>
              )}
            </div>
          )}
          {scope.referenceUrl && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Reference URL
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                <a 
                  href={scope.referenceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: theme.primary || '#7dd3fc',
                    textDecoration: 'none',
                    wordBreak: 'break-all'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  {scope.referenceUrl}
                </a>
              </div>
            </div>
          )}
          {scopeComponents.length > 0 && (
            <div>
              <div style={{ fontSize: '0.75rem', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                Components
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary }}>
                {scopeComponents.length}
              </div>
            </div>
          )}
        </div>

        {/* Title */}
        <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${theme.gridLine}` }}>
          <h3 style={{ 
            fontSize: '1.1rem', 
            fontWeight: '600',
            color: theme.textPrimary,
            marginBottom: '0.75rem'
          }}>
            Title
          </h3>
          <div style={{ 
            padding: '1rem',
            backgroundColor: theme.surface,
            borderRadius: '6px',
            border: `1px solid ${theme.gridLine}`,
            fontSize: '1rem',
            fontWeight: '600',
            color: theme.textPrimary
          }}>
            {scope.title}
          </div>
        </div>

        {/* User Story */}
        {scope.userStory && (
          <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${theme.gridLine}` }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '0.75rem'
            }}>
              User Story
            </h3>
            <div style={{ 
              padding: '1rem',
              backgroundColor: theme.surface,
              borderRadius: '6px',
              border: `1px solid ${theme.gridLine}`,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontSize: '0.9rem',
              lineHeight: '1.6',
              color: theme.textPrimary
            }}>
              {scope.userStory}
            </div>
          </div>
        )}

        {/* Requirements */}
        {scope.requirements && (
          <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${theme.gridLine}` }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '0.75rem'
            }}>
              Requirements
            </h3>
            <div style={{ 
              padding: '1rem',
              backgroundColor: theme.surface,
              borderRadius: '6px',
              border: `1px solid ${theme.gridLine}`,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontSize: '0.9rem',
              lineHeight: '1.6',
              color: theme.textPrimary
            }}>
              {scope.requirements}
            </div>
          </div>
        )}

        {/* Risk Areas */}
        {scope.riskAreas && (
          <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${theme.gridLine}` }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '0.75rem'
            }}>
              Risk Areas
            </h3>
            <div style={{ 
              padding: '1rem',
              backgroundColor: theme.surface,
              borderRadius: '6px',
              border: `1px solid ${theme.gridLine}`,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontSize: '0.9rem',
              lineHeight: '1.6',
              color: theme.textPrimary
            }}>
              {scope.riskAreas}
            </div>
          </div>
        )}

        {/* Questions & Answers */}
        {scope.questions && Array.isArray(scope.questions) && scope.questions.length > 0 && (
          <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${theme.gridLine}` }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '1rem'
            }}>
              Questions & Answers
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {scope.questions.map((qa, index) => (
                <div
                  key={qa.id || index}
                  style={{
                    padding: '1rem',
                    backgroundColor: theme.surface,
                    borderRadius: '6px',
                    border: `1px solid ${theme.gridLine}`
                  }}
                >
                  <div style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '600',
                    color: theme.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.5rem'
                  }}>
                    Question {index + 1}
                  </div>
                  <div style={{
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    color: theme.textPrimary,
                    marginBottom: '0.75rem',
                    paddingBottom: '0.75rem',
                    borderBottom: `1px solid ${theme.gridLine}`
                  }}>
                    {qa.question || <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>No question provided</span>}
                  </div>
                  {qa.answer ? (
                    <div style={{
                      fontSize: '0.9rem',
                      color: theme.textPrimary,
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      lineHeight: '1.6',
                      padding: '0.75rem',
                      backgroundColor: theme.surfaceAlt || theme.surface,
                      borderRadius: '4px',
                      border: `1px solid ${theme.gridLine}`
                    }}>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: '600',
                        color: theme.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.5rem'
                      }}>
                        Answer
                      </div>
                      {qa.answer}
                    </div>
                  ) : (
                    <div style={{
                      fontSize: '0.875rem',
                      color: theme.textMuted,
                      fontStyle: 'italic',
                      padding: '0.75rem',
                      backgroundColor: theme.surfaceAlt || theme.surface,
                      borderRadius: '4px',
                      border: `1px dashed ${theme.gridLine}`
                    }}>
                      No answer provided yet
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Components */}
        {scopeComponents.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: `1px solid ${theme.gridLine}`
            }}>
              Components ({scopeComponents.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {scopeComponents.map(component => {
                const feature = features.find(f => f.id === component.featureId);
                const note = scope.componentNotes?.[component.id];
                const componentTestCases = getTestCasesForComponent(component.id);
                return (
                  <div
                    key={component.id}
                    style={{
                      padding: '1rem',
                      backgroundColor: theme.surface,
                      border: `1px solid ${theme.gridLine}`,
                      borderRadius: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                        <span style={{ 
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          color: theme.textPrimary
                        }}>
                          {component.name}
                        </span>
                        {feature && (
                          <a
                            href={`#/feature/${feature.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/feature/${feature.id}`);
                            }}
                            className="badge badge-cyan"
                            style={{ 
                              textDecoration: 'none',
                              fontSize: '0.75rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = 'var(--border-glow)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {feature.name}
                          </a>
                        )}
                      </div>
                    </div>
                    {note && (
                      <div style={{ 
                        color: theme.textSecondary, 
                        fontSize: '0.875rem', 
                        marginTop: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: theme.surfaceElevated,
                        borderRadius: '4px',
                        fontStyle: 'italic',
                        whiteSpace: 'pre-wrap',
                        borderLeft: `3px solid ${theme.cyanDim}`
                      }}>
                        <div style={{ 
                          fontSize: '0.75rem',
                          color: theme.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '0.25rem'
                        }}>
                          Note
                        </div>
                        {note}
                      </div>
                    )}
                    
                    {/* Test Cases Section */}
                    <div style={{ 
                      marginTop: '0.75rem', 
                      paddingTop: '0.75rem', 
                      borderTop: `1px solid ${theme.gridLine}` 
                    }}>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: theme.textSecondary,
                        marginBottom: '0.5rem'
                      }}>
                        Test Cases ({componentTestCases.length})
                      </div>
                      {componentTestCases.length === 0 ? (
                        <div style={{ 
                          padding: '0.75rem', 
                          backgroundColor: theme.surfaceElevated, 
                          borderRadius: '4px', 
                          fontSize: '0.875rem', 
                          color: theme.textMuted, 
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          No test cases for this component
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {componentTestCases.map(testCase => {
                            return (
                              <div
                                key={testCase.id}
                                style={{
                                  padding: '0.75rem',
                                  backgroundColor: theme.surfaceElevated,
                                  borderRadius: '4px',
                                  border: `1px solid ${theme.gridLine}`
                                }}
                              >
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.5rem',
                                  marginBottom: testCase.description ? '0.25rem' : 0
                                }}>
                                  {testCase.testCaseNumber && (
                                    <span style={{
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      padding: '0.15rem 0.35rem',
                                      backgroundColor: theme.cyanGlow,
                                      color: theme.surface,
                                      borderRadius: '3px',
                                      fontFamily: 'monospace',
                                      flexShrink: 0
                                    }}>
                                      {projectCode ? `${projectCode}-TC-${testCase.testCaseNumber}` : `TC-${testCase.testCaseNumber}`}
                                    </span>
                                  )}
                                  <div style={{ 
                                    fontSize: '0.875rem', 
                                    fontWeight: '500', 
                                    color: theme.textPrimary,
                                    flex: 1
                                  }}>
                                    {testCase.title}
                                  </div>
                                  {testCase.steps && testCase.steps.length > 0 && (
                                    <span style={{ 
                                      fontSize: '0.65rem', 
                                      padding: '0.1rem 0.3rem', 
                                      backgroundColor: 'rgba(0, 212, 255, 0.2)', 
                                      color: theme.cyanGlow, 
                                      borderRadius: '3px',
                                      flexShrink: 0
                                    }}>
                                      {testCase.steps.length} steps
                                    </span>
                                  )}
                                </div>
                                {testCase.description && (
                                  <div style={{ 
                                    fontSize: '0.75rem', 
                                    color: theme.textSecondary, 
                                    marginTop: '0.25rem',
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word'
                                  }}>
                                    {testCase.description}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Linked Completed Sessions */}
        {linkedSessions.length > 0 && (
          <div style={{ 
            marginTop: '2rem', 
            paddingTop: '1.5rem', 
            borderTop: `1px solid ${theme.gridLine}`
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '1rem'
            }}>
              Completed Sessions ({linkedSessions.length})
            </h3>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {linkedSessions.map((session) => (
                <div
                  key={session.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: theme.surfaceElevated,
                    borderRadius: '4px',
                    border: `1px solid ${theme.gridLine}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    setSelectedSession(session);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = theme.cyanGlow;
                    e.currentTarget.style.backgroundColor = theme.cyanTransparent;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.gridLine;
                    e.currentTarget.style.backgroundColor = theme.surfaceElevated;
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '0.5rem'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: theme.textMuted, 
                        fontFamily: 'monospace',
                        marginBottom: '0.25rem'
                      }}>
                        {session.id}
                      </div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600',
                        color: theme.textPrimary,
                        marginBottom: '0.25rem'
                      }}>
                        {session.sessionType}
                      </div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: theme.textSecondary
                      }}>
                        {session.user} |{' '}
                        {session.platforms && session.platforms.length > 0
                          ? session.platforms.join(', ')
                          : session.platform || 'N/A'}
                        {session.milestone && ` | ${session.milestone}`}
                      </div>
                      {session.completedAt && (
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: theme.textMuted,
                          marginTop: '0.25rem'
                        }}>
                          Completed: {formatDate(session.completedAt, 'MMM dd, yyyy')}
                        </div>
                      )}
                    </div>
                    <span className={`status-badge status-${session.status}`} style={{ flexShrink: 0 }}>
                      {session.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
      </div>
      
      {/* Session Details Modal */}
      {selectedSession && (
        <SessionDetails
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onBackToTestPlan={() => setSelectedSession(null)}
          userRole={userRole}
          zIndex={zIndex + 1}
        />
      )}
    </div>
  );
};

export default TestPlanDetails;
